import React from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { defineMessages, injectIntl, WrappedComponentProps } from 'react-intl';
import { Moon, Sun } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { ColorMode } from '../../redux/modules/colorMode/reducer';
import { getTestProps } from '../../util/testProps';

export const testIds = {
  button: 'darkModeSwitch-toggleId',
};

const styles = (_theme: Theme) =>
  createStyles({
    iconContainer: {
      display: 'inline-block',
      '&:hover': {
        cursor: 'pointer',
      },
      '&:focus': {
        outline: 'none',
      },
    },
  });

const messages = defineMessages({
  toggleDarkMode: {
    id: 'darkModeSwitch.toggleDarkMode',
    defaultMessage: 'Toggle dark mode',
  },
  enableDarkMode: {
    id: 'darkModeSwitch.enableDarkMode',
    defaultMessage: 'Switch to dark mode',
  },
  enableLightMode: {
    id: 'darkModeSwitch.enableLightMode',
    defaultMessage: 'Switch to light mode',
  },
});

export interface DarkModeSwitchOwnProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  setColorMode(mode: ColorMode): void;
  colorMode: ColorMode;
}

export type DarkModeSwitchInjectedProps = WithStyles<typeof styles> &
  WrappedComponentProps;

export type DarkModeSwitchProps = DarkModeSwitchOwnProps &
  DarkModeSwitchInjectedProps;

export class DarkModeSwitchComponent extends React.PureComponent<DarkModeSwitchProps> {
  private handleToggleColorMode = () => {
    const { setColorMode, colorMode } = this.props;
    setColorMode(colorMode === 'dark' ? 'light' : 'dark');
  };

  public render() {
    const {
      classes,
      colorMode,
      intl: { formatMessage },
      setColorMode,
      ...rootProps
    } = this.props;

    const isDark = colorMode === 'dark';
    const tooltip = formatMessage(
      isDark ? messages.enableLightMode : messages.enableDarkMode,
    );
    const ModeIcon = isDark ? Sun : Moon;

    return (
      <div {...rootProps}>
        <Tooltip title={tooltip} placement="left">
          <div className={classes.iconContainer}>
            <IconButton
              id="dark-mode-toggle"
              aria-label={formatMessage(messages.toggleDarkMode)}
              color="inverse"
              onClick={this.handleToggleColorMode}
              icon={ModeIcon}
              size={0}
              {...getTestProps(testIds.button)}
            />
          </div>
        </Tooltip>
      </div>
    );
  }
}

export default injectIntl(withStyles(styles)(DarkModeSwitchComponent));
